// src/types.ts

export type ViewState = 
  | 'HUB' 
  | 'PRODUCT_CODETOTEXT' 
  | 'PRODUCT_ASSEMBLAGE' 
  | 'PRODUCT_FACTORY'
  | 'PRODUCT_BUILDLOG' 
  | 'BLOG' 
  | 'BLOG_POST' 
  | 'ADMIN' 
  | 'LOGIN' 
  | 'USER_DASHBOARD';

export interface Product {
  id: string;
  name: string;
  tagline: string;
  status: 'LIVE' | 'BETA' | 'DEV' | 'DESIGN' | 'CONCEPT' | 'PLANNING';
  description: string;
  badge?: string;
  path: ViewState;
  imageUrl?: string;
}

export interface PricingTier {
  name: string;
  price: string;
  period: string;
  features: string[];
  highlight?: boolean;
}

// ── Content Pillar / Cluster taxonomy ───────────────────────────────────────
// Single taxonomy for both written and video content. Replaces the old
// editorial ContentPillar (Technical Authority, Founder Transparency, etc.)
// and the old VideoPlaylist union entirely — both were separate, unrelated
// groupings that could drift out of sync with each other and with the actual
// content strategy. This is the SEO hub-and-spoke structure: 4 pillar pages,
// each with 3–5 clusters as sections within that page, each cluster
// containing one or more articles or videos.

// The 4 top-level content pillars. Each has exactly one pillar-page URL.
// Videos group at the pillar level only (see BlogPost.playlist below) —
// there is no separate video-specific taxonomy.
export type ContentPillar =
  | 'protocol-architecture'   // Pillar 1 — Protocol & Architecture (developer)
  | 'copilot-vs-autopilot'    // Pillar 2 — Copilot vs. Autopilot (founder/investor)
  | 'vertical-playbooks'      // Pillar 3 — Vertical Playbooks (merchant)
  | 'trust-control';          // Pillar 4 — Trust & Control (merchant/consumer)

// Clusters are sections within a pillar page, not standalone URLs.
// Written content (articles) is grouped at this granularity; video content
// is grouped one level up, at the pillar (see ContentPillar / playlist).
export type ContentCluster =
  | 'nine-stage-protocol'
  | 'rules-of-engagement'
  | 'merchant-of-record'
  | 'human-principal-architecture'
  | 'protocol-interoperability'
  | 'copilot-autopilot-defined'
  | 'cluster-framework'
  | 'last-mile-gap'
  | 'vertical-sequencing'
  | 'transactional-legal'
  | 'insurance-brokerage'
  | 'accounting-close'
  | 'tax-advisory'
  | 'ask-first-boundaries'
  | 'audit-trail-asset'
  | 'never-merchant-of-record'
  | 'unauthenticated-cell';

export interface PillarMeta {
  label: string;
  description: string;
  audience: string;
  order: number;
}

export const PILLAR_META: Record<ContentPillar, PillarMeta> = {
  'protocol-architecture': {
    label: 'Protocol & Architecture',
    description: 'The technical "how it works" pillar — the 9-stage protocol, Capability Cards, and the governed-delegation architecture.',
    audience: 'developer',
    order: 1,
  },
  'copilot-vs-autopilot': {
    label: 'Copilot vs. Autopilot',
    description: 'The strategic-thesis pillar — a sharper framework for reasoning about agent autonomy than industry labels provide.',
    audience: 'founder/investor',
    order: 2,
  },
  'vertical-playbooks': {
    label: 'Vertical Playbooks',
    description: 'One cluster per professional vertical, written to the actual practitioner, not a size category.',
    audience: 'merchant',
    order: 3,
  },
  'trust-control': {
    label: 'Trust & Control',
    description: 'The differentiator-vs-generic-agent-platforms pillar — control, not just discoverability.',
    audience: 'merchant/consumer',
    order: 4,
  },
};

export interface ClusterMeta {
  label: string;
  description: string;
  pillar: ContentPillar;
  order: number;
}

export const CLUSTER_META: Record<ContentCluster, ClusterMeta> = {
  'nine-stage-protocol': {
    label: 'The 9-Stage Protocol',
    description: 'One deep post per stage, or a canonical explainer plus stage-specific deep dives — Discovery, Qualification, Commitment, and Fulfillment have the most to say.',
    pillar: 'protocol-architecture',
    order: 1,
  },
  'rules-of-engagement': {
    label: 'Rules of Engagement as Code',
    description: 'Inform Only / Ask First / Act Within Limits — how the dial actually gates tool calls.',
    pillar: 'protocol-architecture',
    order: 2,
  },
  'merchant-of-record': {
    label: 'Merchant of Record & Settlement Mechanics',
    description: 'Why FDW never touches funds, Stripe Direct Charges, and what that means architecturally.',
    pillar: 'protocol-architecture',
    order: 3,
  },
  'human-principal-architecture': {
    label: 'Human Principal Architecture',
    description: 'The governance model, written for engineers evaluating whether to build on FDW.',
    pillar: 'protocol-architecture',
    order: 4,
  },
  'protocol-interoperability': {
    label: 'MCP / Agent-Protocol Interoperability',
    description: 'How FDW fits into the broader agent-tooling ecosystem — UCP, MCP, and beyond.',
    pillar: 'protocol-architecture',
    order: 5,
  },
  'copilot-autopilot-defined': {
    label: 'Copilot vs. Autopilot, Defined Precisely',
    description: 'The lawyer/client worked example — correcting a common misreading that autonomy equals business model.',
    pillar: 'copilot-vs-autopilot',
    order: 1,
  },
  'cluster-framework': {
    label: 'The Cluster Framework',
    description: 'Detection-against-fixed-reference, rule-application, negotiation-under-uncertainty, and comparative/optimization — a sharper lens than industry labels.',
    pillar: 'copilot-vs-autopilot',
    order: 2,
  },
  'last-mile-gap': {
    label: 'The Last-Mile Gap',
    description: 'What FDW owns (stages 1–6, 8–9) versus what it doesn\'t (stage 7) — a deliberate boundary, not a shortcoming.',
    pillar: 'copilot-vs-autopilot',
    order: 3,
  },
  'vertical-sequencing': {
    label: 'Vertical Sequencing, Published Incrementally',
    description: '"Why we\'re starting here" posts as each vertical locks, published as confidence is earned rather than as one ranked list up front.',
    pillar: 'copilot-vs-autopilot',
    order: 4,
  },
  'transactional-legal': {
    label: 'Transactional Legal',
    description: 'The redline/NDA case — human-in-loop framed explicitly as the current right answer, not a limitation.',
    pillar: 'vertical-playbooks',
    order: 1,
  },
  'insurance-brokerage': {
    label: 'Insurance Brokerage & Claims',
    description: 'The strongest-evidenced cluster — a flagship case study once verification is complete.',
    pillar: 'vertical-playbooks',
    order: 2,
  },
  'accounting-close': {
    label: 'Accounting & Close',
    description: 'The "nobody\'s built this yet" opportunity, framed as a call to early-adopter merchants.',
    pillar: 'vertical-playbooks',
    order: 3,
  },
  'tax-advisory': {
    label: 'Tax Advisory',
    description: 'The multi-jurisdiction data moat angle.',
    pillar: 'vertical-playbooks',
    order: 4,
  },
  'ask-first-boundaries': {
    label: 'What "Ask First" Actually Stops',
    description: 'A concrete before/after of an agent hitting a boundary.',
    pillar: 'trust-control',
    order: 1,
  },
  'audit-trail-asset': {
    label: 'Audit Trail as a Client-Shareable Asset',
    description: 'The Stage 9 trust loop, positioned as a sales asset for the merchant, not just a compliance log.',
    pillar: 'trust-control',
    order: 2,
  },
  'never-merchant-of-record': {
    label: 'Why FDW Is Never the Merchant of Record',
    description: 'A direct SEO play against "is my AI platform trustworthy with money" search intent.',
    pillar: 'trust-control',
    order: 3,
  },
  'unauthenticated-cell': {
    label: 'The Unauthenticated Cell Problem',
    description: 'Cell 4 of the FDW Grid — nobody accountable — as a stark contrast to how FDW\'s authority chain prevents that failure mode.',
    pillar: 'trust-control',
    order: 4,
  },
};

// Single authoritative BlogPost interface.
// Previously declared twice in this file — the first (legacy subset) has been removed.
export interface BlogPost {
  id: string;
  title: string;
  slug?: string;
  excerpt: string;
  content: string;                  // Markdown/HTML content
  category?: string;                // Kept for backward compatibility — maps to playlist
  createdAt?: string;
  updatedAt?: string;
  readTime?: string;
  authorId?: string;
  authorName?: string;
  authorPhoto?: string;
  coverImageUrl?: string;
  tags?: string[];
  product?: 'codetotext' | 'assemblage' | 'buildlog' | 'company';
  isPublic?: boolean;               // If true, visible to visitors without login
  canonicalUrl?: string;

  // ── YouTube & Strategy ─────────────────────────────────────────────────────
  youtubeId?: string;               // e.g. dQw4w9WgXcQ
  // Video grouping — now the same 4-pillar taxonomy as written content.
  // Was VideoPlaylist (a separate, unrelated union); dropped in favor of
  // reusing ContentPillar directly so videos and articles can never drift
  // out of sync with each other.
  playlist?: ContentPillar;
  psychologicalTrigger?: 'Curiosity Gap' | 'Social Proof' | 'Transparency' | 'Authority';

  // ── SEO Pillar / Cluster taxonomy ───────────────────────────────────────
  // Which of the 4 pillars this post belongs to — set on the pillar page
  // itself and on every article beneath it.
  contentPillar?: ContentPillar;

  // Which cluster/section within that pillar this article belongs to.
  // Omit on the pillar page itself — it contains all clusters as sections.
  contentCluster?: ContentCluster;

  // ── Infrastructure Metrics ─────────────────────────────────────────────────
  metrics?: {
    revenue?: string;               // e.g. "$2,400 MRR"
    traffic?: string;               // e.g. "12k Visits"
    velocity?: string;              // e.g. "48hrs to Live"
  };

  // ── FAQ / E-E-A-T ──────────────────────────────────────────────────────────
  // Keyed by audience slug, value is the canonical question string.
  // Written by the Buildlog → FDW publish trigger from the source Buildlog entry.
  // Injected as FAQPage JSON-LD schema at static build time (generate-blog-pages.js)
  // and in the runtime React view (BlogView.tsx → FAQSchema).
  // Example: { developer: "How does a draft get published?", founder: "How does content reach Google?" }
  canonicalQuestions?: Record<string, string>;
}

export interface UserProfile {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
  isPro: boolean;
  isAdmin?: boolean; //

  usageCount?: number;
  usageLimit?: number;
  usageResetDate?: string;
  subscriptionId?: string;

  // Referral tracking parameters
  bankedFreeMonths?: number;
  proUntil?: any;

  isPro_codetotext?: boolean;   // Specific application flag matching your snapshot
  plan?: string;                // Plan tier literal (e.g., "pro")
  stripeCustomerId?: string;
}

export type UserRole = 'Admin' | 'Editor' | 'Viewer';

export interface TeamMember {
  id: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  role: UserRole;
  status: 'Active' | 'Invited' | 'Suspended';
  dateAdded: string;
}

export interface FactoryDrop {
  id: string;
  title: string;
  desc: string;
  type: string;                     // e.g. 'Config', 'Template', 'Workflow'
  date: string;
  iconType: 'json' | 'code' | 'box';
  downloadUrl?: string;
}

export interface ProductionItem {
  id: string;
  name: string;
  status: 'Concept' | 'In Dev' | 'Beta' | 'Shipped';
  votes: number;
  userVoted?: boolean;              // Client-side state only
}